import React, { useState, useEffect } from 'react'
import { Modal, Table, Button, message, Tag, Card, Spin } from 'antd'
import { hookService } from 'services/hookService'
import { withNamespaces } from 'react-i18next'
import moment from 'moment'
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter'
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json'
import { arta } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import {
  PlanningRiskActions,
  isPlanningRisks,
  triggerLabel,
} from 'containers/Setup/Hooks/planningRisks'
import { RetryHookEventButton, RetryInfo } from 'components/HookEventRetry'

SyntaxHighlighter.registerLanguage('json', json)

const HOOK_EVENTS = [
  {
    id: 0,
    name: 'ProductionAlert',
    description: 'Triggered when any work station alert is invoked by any operator.',
  },
  {
    id: 1,
    name: 'WorkOrderPlanned',
    description: 'Triggered when a work order is schedule, unschedule, shift.',
  },
  {
    id: 2,
    name: 'WorkOrderExecutionBegin',
    description: 'Triggered when a work order start the production.',
  },
  {
    id: 3,
    name: 'WorkOrderExecutionPaused',
    description: 'Triggered when a work order pause the production.',
  },
  {
    id: 4,
    name: 'WorkOrderExecutionEnded',
    description: 'Triggered when the flow of a work order is concluded.',
  },
  { id: 5, name: 'WorkOrderReset', description: 'Triggered when a work order is reset.' },
  {
    id: 6,
    name: 'WorkOrderConsumedRawMaterialUpdated',
    description:
      'Triggered when a consumed raw material has any update, even when the work order is closed.',
  },
  {
    id: 7,
    name: 'WorkOrderExecutionResumed',
    description: 'Triggered when a paused work order is resumed.',
  },
  {
    id: 8,
    name: 'MaterialSupplyUpdated',
    description: 'Triggered when a material supply is payed.',
  },
  {
    id: 9,
    name: 'WorkOrderProducedQuantityUpdated',
    description:
      'Triggered when the work order produced quantity is updated though the PlantScanner Portal.',
  },
  {
    id: 10,
    name: 'CanWorkOrderFinalize',
    description:
      'This hook is triggered every time an operator tries to complete the work order, you can use this hook when you want to perform custom checks whether or not that specific order can be completed.',
  },
  {
    id: 11,
    name: 'CanWorkOrderTrackQuantity',
    description:
      'This hook is activated whenever an operator attempts to record the produced good or bad quantities in a work order. You can utilize this hook to perform custom checks to determine whether a specific order can be tracked.',
  },
  {
    id: 12,
    name: 'PrintedTag',
    description: 'Triggered when an operator prints a tag',
  },
  {
    id: 13,
    name: 'CanWorkOrderStartExecution',
    description:
      'This hook is triggered every time an operator tries to start a work order, you can use this hook when you want to perform custom checks whether or not that specific order can be started.',
  },
  {
    id: 14,
    name: 'IntegrateWorkOrders',
    description:
      'Called when someone taps "Sync now" on the home screen. The webhook pulls the production orders from the ERP and answers how many were synced and which codes.',
  },
  {
    id: 15,
    name: 'PlanningRisks',
    description:
      'E-mail with the orders that will miss the delivery date, are late on the schedule or lack material: daily summary at 7am and an alert when a new risk shows up.',
  },
]

const HookDetailsModal = ({ isOpen, onClose, hookId, workStations, t }) => {
  const [loading, setLoading] = useState(false)
  const [eventsLoading, setEventsLoading] = useState(false)
  const [details, setDetails] = useState(null)
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })
  const [events, setEvents] = useState([])

  useEffect(() => {
    if (isOpen && hookId) {
      loadHookDetails()
      loadEvents()
    } else {
      setDetails(null)
      setEvents([])
    }
  }, [isOpen, hookId])

  const loadHookDetails = async () => {
    try {
      setLoading(true)
      const response = await hookService.getHookDetails(hookId)
      setDetails(response.data)
    } catch (error) {
      message.error(t('hooks.loadError'))
    } finally {
      setLoading(false)
    }
  }

  // page/pageSize vêm da troca de página: o estado da paginação só muda depois desta chamada
  const loadEvents = async (page = pagination.current, pageSize = pagination.pageSize) => {
    try {
      setEventsLoading(true)
      const response = await hookService.getHookEvents(hookId, page, pageSize)
      setEvents(response.data.events)
      setPagination((prev) => ({
        ...prev,
        total: response.data.totalCount,
      }))
    } catch (error) {
      message.error(t('hooks.loadError'))
    } finally {
      setEventsLoading(false)
    }
  }

  const toggleExpandRow = (recordId) => {
    const newExpandedRows = new Set(expandedRows)
    if (newExpandedRows.has(recordId)) {
      newExpandedRows.delete(recordId)
    } else {
      newExpandedRows.add(recordId)
    }
    setExpandedRows(newExpandedRows)
  }

  const formatPayload = (payload) => {
    try {
      if (typeof payload === 'string') {
        return JSON.stringify(JSON.parse(payload), null, 2)
      }
      return JSON.stringify(payload, null, 2)
    } catch (e) {
      return payload
    }
  }

  const getAuthTypeText = (type) => {
    switch (type) {
      case 0:
        return t('hooks.authType.undefined')
      case 1:
        return t('hooks.authType.basic')
      case 2:
        return t('hooks.authType.bearer')
      case 3:
        return t('hooks.authType.oauth')
      default:
        return type
    }
  }

  const getTriggerTypeText = (type) => triggerLabel(t, type)

  const getWorkStationNames = (workStationIds) => {
    if (!workStationIds || !workStations) return ''
    return workStationIds
      .map((id) => workStations.find((ws) => ws.id === id)?.description)
      .filter(Boolean)
      .join(', ')
  }

  const getEventInfo = (hookType) => {
    const event = HOOK_EVENTS.find((e) => e.id === hookType)
    if (!event) return { name: hookType, description: '' }
    return {
      name: t(`hooks.events.${event.name}.name`),
      description: t(`hooks.events.${event.name}.description`),
    }
  }

  const columns = [
    {
      title: t('hooks.details.timestamp'),
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: '20%',
      // a API devolve UTC sem fuso
      render: (date) => moment.utc(date).local().format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: t('hooks.status.title'),
      dataIndex: 'resultCode',
      key: 'resultCode',
      width: '20%',
      render: (code, record) => (
        <div
          style={{ display: 'flex', alignItems: 'center', maxWidth: '100px', overflow: 'hidden' }}
          title={record.resultMessage}
        >
          <Tag
            color={record.sending ? 'processing' : code >= 200 && code < 300 ? 'success' : 'error'}
          >
            {code} - {record.resultMessage}
          </Tag>
        </div>
      ),
    },
    {
      title: t('hooks.retry.column', 'Reenvio'),
      key: 'retry',
      width: 170,
      render: (_, record) => (
        <>
          <RetryHookEventButton
            event={record}
            onRetried={() => {
              setPagination((prev) => ({ ...prev, current: 1 }))
              loadEvents(1, pagination.pageSize)
            }}
          />
          <RetryInfo event={record} />
        </>
      ),
    },
    {
      title: t('hooks.details.payload'),
      dataIndex: 'payload',
      key: 'payload',
      width: '45%',
      render: (payload, record) => {
        const isExpanded = expandedRows.has(record.id)
        const formattedPayload = formatPayload(payload)

        return (
          <div style={{ position: 'relative', width: '100%', maxWidth: '600px' }}>
            <div
              style={{
                position: 'relative',
                maxHeight: isExpanded ? 'none' : '100px',
                overflow: 'hidden',
                width: '100%',
              }}
            >
              <SyntaxHighlighter
                language="json"
                style={arta}
                customStyle={{
                  margin: 0,
                  padding: '8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  width: '100%',
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                }}
                wrapLines={true}
              >
                {formattedPayload}
              </SyntaxHighlighter>
            </div>
            {formattedPayload.split('\n').length > 5 && (
              <Button
                type="link"
                onClick={() => toggleExpandRow(record.id)}
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#fff',
                  boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
                  display: 'block',
                  width: '100%',
                  textAlign: 'center',
                  borderRadius: '0 0 4px 4px',
                  zIndex: 1,
                }}
              >
                {isExpanded ? t('hooks.details.seeLess') : t('hooks.details.seeMore')}
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  const handleTableChange = (next) => {
    setPagination(next)
    loadEvents(next.current, next.pageSize)
  }

  return (
    <Modal
      title={t('hooks.details.title')}
      open={isOpen}
      onCancel={onClose}
      width={1200}
      footer={[
        <Button key="close" onClick={onClose}>
          {t('hooks.details.close')}
        </Button>,
      ]}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
        </div>
      ) : (
        details && (
          <>
            <Card style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  {isPlanningRisks(details.hook.hookType) && (
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                      {t('hooks.planningRisks.recipients', 'E-mails que recebem')}
                    </div>
                  )}
                  <h3 style={{ margin: '0 0 16px 0' }}>{details.hook.address}</h3>
                  {isPlanningRisks(details.hook.hookType) && (
                    <div style={{ marginBottom: 8 }}>
                      <PlanningRiskActions
                        t={t}
                        hookId={details.hook.id}
                        onSent={() => loadEvents()}
                      />
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div>
                    <strong>{t('hooks.eventType')}:</strong>
                    <div style={{ marginTop: 8 }}>
                      {(() => {
                        const eventInfo = getEventInfo(details.hook.hookType)
                        return (
                          <>
                            <div>{eventInfo.name}</div>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                              {eventInfo.description}
                            </div>
                          </>
                        )
                      })()}
                    </div>
                  </div>

                  <div>
                    <strong>{t('hooks.triggerType.title')}:</strong>
                    <div style={{ marginTop: 8 }}>
                      {getTriggerTypeText(details.hook.hookTrigger)}
                    </div>
                  </div>

                  <div>
                    <strong>{t('hooks.authType.title')}:</strong>
                    <div style={{ marginTop: 8 }}>{getAuthTypeText(details.hook.authType)}</div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '16px',
                    marginTop: 8,
                  }}
                >
                  <div>
                    <p>
                      <strong>{t('hooks.status.title')}:</strong>{' '}
                      <Tag color={details.hook.isActive ? 'success' : 'default'}>
                        {details.hook.isActive
                          ? t('hooks.status.active')
                          : t('hooks.status.inactive')}
                      </Tag>
                    </p>
                    <p>
                      <strong>{t('hooks.modal.form.allWorkStations')}:</strong>{' '}
                      {details.hook.allWorkStations ? '✓' : '✗'}
                    </p>
                    {!details.hook.allWorkStations && (
                      <div>
                        <strong>{t('hooks.modal.form.workStations')}</strong>
                        <p>{getWorkStationNames(details.hook.workStationsIds)}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <p>
                      <strong>{t('hooks.details.createdAt')}:</strong>{' '}
                      {moment.utc(details.hook.createdDate).local().format('DD/MM/YYYY HH:mm:ss')}
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <h4 style={{ margin: '0 0 16px 0' }}>{t('hooks.details.events')}</h4>

            {events && events.length > 0 ? (
              <Table
                columns={columns}
                dataSource={events}
                loading={eventsLoading}
                rowKey="id"
                pagination={{
                  ...pagination,
                  showSizeChanger: true,
                  showTotal: (total) => t('hooks.details.totalEvents', { total }),
                  pageSizeOptions: ['10', '20', '50', '100'],
                }}
                onChange={handleTableChange}
                scroll={{ x: true }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                {eventsLoading ? <Spin /> : t('hooks.details.noEvents')}
              </div>
            )}
          </>
        )
      )}
    </Modal>
  )
}

export default withNamespaces()(HookDetailsModal)
